/**
 * Primary and secondary diagnoses are one ordered list, `[primary, ...secondaries]`,
 * in which flat position 0 IS the primary. Every drop is an insertion, never a
 * swap: promoting a secondary pushes the old primary down to the first secondary
 * slot, and demoting the primary promotes the first secondary.
 */
import { arrayMove } from '@dnd-kit/sortable';
import type {
  DiagnosisWithReasoning,
  PrimaryDiagnosis,
} from '../types/codingData.types';

export const PRIMARY_SLOT_ID = 'diagnosis-primary';

export const secondarySlotId = (index: number): string => `diagnosis-${index}`;

export interface DiagnosisOrder {
  primary: PrimaryDiagnosis;
  secondaries: DiagnosisWithReasoning[];
}

/** Flat position of a sortable slot id; null when the id is not a diagnosis slot. */
export function flatIndexForSlotId(id: string): number | null {
  if (id === PRIMARY_SLOT_ID) return 0;
  const match = /^diagnosis-(\d+)$/.exec(id);
  return match ? Number(match[1]) + 1 : null;
}

/** Returns null when the move is a no-op or out of bounds. Inputs are never mutated. */
export function applyDiagnosisReorder(
  primary: PrimaryDiagnosis,
  secondaries: DiagnosisWithReasoning[],
  fromFlat: number,
  toFlat: number
): DiagnosisOrder | null {
  const flat: DiagnosisWithReasoning[] = [primary, ...secondaries];
  const inBounds = (index: number) =>
    Number.isInteger(index) && index >= 0 && index < flat.length;

  if (fromFlat === toFlat || !inBounds(fromFlat) || !inBounds(toFlat)) {
    return null;
  }

  const [nextPrimary, ...nextSecondaries] = arrayMove(flat, fromFlat, toFlat);
  return { primary: nextPrimary, secondaries: nextSecondaries };
}
